import { useCallback, useEffect, useState } from 'react';
import { usePromptGroups } from '../../promptsLibrary/usePromptGroups';
import { PromptGroup_deprecated } from '@server/prompts';
import isEqual from 'lodash/isEqual';

export const usePromptLibraryCard = ({ promptGroup }: { promptGroup: PromptGroup_deprecated }) => {
  const [promptGroupInEdit, setPromptGroupInEdit] = useState<PromptGroup_deprecated | null>(null);
  const [showNameErrorState, setShowNameErrorState] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [initialCardState, setInitialCardState] = useState<PromptGroup_deprecated | null>(null);

  const {
    createPromptGroup__deprecated,
    updatePromptGroup,
    removeEmptyPromptGroup__deprecated,
    removePromptGroup,
  } = usePromptGroups();

  const startEdit = useCallback((group: PromptGroup_deprecated) => {
    setInitialCardState({ ...group });
    setPromptGroupInEdit({ ...group });
  }, []);

  const cancelEdit = useCallback(() => {
    setPromptGroupInEdit(null);
    setInitialCardState(null);
    setShowNameErrorState(false);
  }, []);

  const onEdit = useCallback((key: string, value: string) => {
    if (key === 'title' && value !== '') {
      setShowNameErrorState(false);
    }
    setPromptGroupInEdit((prev) => ({ ...prev, [key]: value }) as PromptGroup_deprecated);
  }, []);

  useEffect(() => {
    if (!promptGroupInEdit && promptGroup.id === '') {
      startEdit(promptGroup);
    }
  }, [promptGroup, promptGroupInEdit, startEdit]);

  const onConfirm = useCallback(
    async (_promptGroup: PromptGroup_deprecated) => {
      const isNewCard = _promptGroup.id === '';
      const isPristine = isEqual(_promptGroup, initialCardState);
      const isCardEmpty = !_promptGroup.name?.trim() && !_promptGroup.description?.trim();

      if (isNewCard && isCardEmpty) {
        removeEmptyPromptGroup__deprecated();
        setPromptGroupInEdit(null);
        setInitialCardState(null);
        setShowNameErrorState(false);
        return;
      }

      if (!isPristine) {
        if (isNewCard) {
          if (_promptGroup.name === '') {
            setShowNameErrorState(true);
            return;
          }

          setIsCardLoading(true);
          await createPromptGroup__deprecated(_promptGroup);
          removeEmptyPromptGroup__deprecated();
        }

        if (!isNewCard) {
          if (_promptGroup.name === '') {
            setShowNameErrorState(true);
            return;
          }
          setIsCardLoading(true);
          await updatePromptGroup(_promptGroup);
        }
      }

      setPromptGroupInEdit(null);
      setInitialCardState(null);
      setShowNameErrorState(false);
      setIsCardLoading(false);
    },
    [
      createPromptGroup__deprecated,
      updatePromptGroup,
      removeEmptyPromptGroup__deprecated,
      initialCardState,
    ],
  );

  const onDeleteCard = useCallback(
    async (promptGroup: PromptGroup_deprecated) => {
      await removePromptGroup(promptGroup.id);
      setPromptGroupInEdit(null);
      setShowNameErrorState(false);
    },
    [removePromptGroup],
  );

  return {
    promptGroupInEdit,
    isCardLoading,
    showNameErrorState,
    startEdit,
    cancelEdit,
    onEdit,
    onConfirm,
    onDeleteCard,
  };
};
